import {
  Button,
  FormCheckbox,
  FormInput,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import type {
  ShippingCarrierCreateInput,
  ShippingCarrierUpdateInput,
} from "@/modules/shipping/domain/contracts";

const CarrierFormSchema = z.object({
  code: z.string().min(1, "ຕ້ອງໃສ່ລະຫັດ"),
  nameLo: z.string().optional(),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  enabled: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

export type CarrierFormValues = z.output<typeof CarrierFormSchema>;

const DEV_FILL: CarrierFormValues = {
  code: "DEMO",
  nameLo: "ຂົນສົ່ງທົດລອງ",
  nameTh: "ขนส่งทดสอบ",
  nameEn: "Demo carrier",
  enabled: true,
  sortOrder: 0,
};

type CarrierFormProps = {
  initialValues?: Partial<CarrierFormValues>;
  onSubmit: (
    values: ShippingCarrierCreateInput | ShippingCarrierUpdateInput,
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function CarrierForm({
  initialValues,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: CarrierFormProps) {
  const methods = RHF.useForm<CarrierFormValues>({
    resolver: zodResolver(CarrierFormSchema) as Resolver<CarrierFormValues>,
    defaultValues: {
      code: "",
      nameLo: "",
      nameTh: "",
      nameEn: "",
      enabled: true,
      sortOrder: 0,
      ...initialValues,
    },
  });

  const fillDev = () => {
    methods.reset(DEV_FILL);
  };

  return (
    <FormRoot<CarrierFormValues>
      methods={methods as UseFormReturn<CarrierFormValues>}
      onSubmit={(vals) =>
        onSubmit({
          code: vals.code,
          name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
          enabled: vals.enabled,
          sortOrder: vals.sortOrder,
        } as ShippingCarrierCreateInput | ShippingCarrierUpdateInput)
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">ຜູ້ໃຫ້ບໍລິການຂົນສົ່ງ</h3>
          {onDevFill && (
            <Button type="button" variant="outline" size="sm" onClick={fillDev}>
              ເຕີມຂໍ້ມູນທົດສອບ
            </Button>
          )}
        </div>
        <FormInput name="code" label="ລະຫັດ (ບັນທຶກເປັນຕົວໃຫຍ່)" placeholder="JNT" />
        <FormInput name="nameLo" label="ຊື່ (ລາວ)" placeholder="ພາສາລາວ" />
        <FormInput name="nameTh" label="ຊື່ (ไทย)" placeholder="ภาษาไทย" />
        <FormInput name="nameEn" label="ຊື່ (EN)" placeholder="English" />
        <FormInput name="sortOrder" label="ລຳດັບ" type="number" min={0} />
        <FormCheckbox name="enabled" label="ເປີດໃຊ້ງານ (ສະແດງໃນເວັບລູກຄ້າ)" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            ບັນທຶກ
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset())}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
